



































































































































































import _trim from 'lodash/trim';
import { Component, Prop, Model, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import { SwIcon, IconConfig } from '../sw-icon';

const cssPrefix = 'sw-textfield';

/**
 * @event input `(value: string)`
 * @event focus `(event: MouseEvent)`
 * @event blur `(event: MouseEvent)`
 * @event keyup `(event: MouseEvent)`
 * @event enter `(event: MouseEvent)`
 * @event icon-click `(event: MouseEvent)`
 */
@Component({
  components: {
    SwIcon,
  },
})
export default class SwTextfield extends SwExtendedVue {
  @Model('input', { type: String, required: true })
  value: string;

  @Prop({ type: String, required: true })
  name: string;

  @Prop({ type: String, default: 'normal' })
  type: 'normal' | 'password' | 'readonly' | 'display';

  @Prop({ type: Boolean, default: false })
  disabled: boolean;

  @Prop({ type: String, default: 'light' })
  theme: 'light' | 'dark';

  @Prop({ type: String, default: '' })
  placeholder: string;

  @Prop({ type: Object })
  icon: IconConfig;

  @Prop({ type: Boolean, default: false })
  iconAsPrefix: boolean;

  @Prop({ type: Boolean, default: false })
  full: boolean;

  @Prop({ type: Boolean, default: false })
  numberOnly: boolean;

  cssPrefix: string = cssPrefix;
  isFocus: boolean = false;

  /**
   * Value of internal intermediate state for caching value before each input event, hence it is
   * a value in between of `value` prop and to-be-emitted value.
   */
  valueBeforeChange: string = this.value;

  toCursorPos: number | null = null;

  inInputTransaction: boolean = false;
  isComposing: boolean = false;

  get isInputReadonly(): boolean {
    return this.type === 'readonly' || this.type === 'display';
  }

  get inputType() {
    return (this.type === 'password' ? 'password' : 'text');
  }

  get effectiveIconConfig(): IconConfig {
    return Object.assign(
      {
        size: 14,
        asBlock: true,
      },
      this.icon,
    );
  }

  @Watch('value')
  private onValueChanged() {
    this.valueBeforeChange = this.value;

    const inputEl = (this.$refs.input as HTMLInputElement);
    if (inputEl) {
      inputEl.value = this.value;
      if (typeof this.toCursorPos === 'number') {
        inputEl.setSelectionRange(this.toCursorPos, this.toCursorPos);
      }
    }
  }

  private isPortionOfNumberExpression(test: string): boolean {
    return /^[+\-]?[0-9]*(\.[0-9]*)?$/.test(test) && !(/^[+\-]?[0-9]+(\.[0-9]+)?$/).test(test);
  }

  private handleInput(inputEl: HTMLInputElement) {
    if (this.isComposing) { return; }
    if (this.inInputTransaction) { return; }

    let changedValue: string = inputEl.value;
    let forcedValue: string = this.valueBeforeChange;
    let doEmit: boolean = true;

    /** force the value of `input` element to be synced with the `value` prop, according to "Controlled Component" concept */
    if (this.numberOnly) {
      changedValue = _trim(changedValue);
      const converted = Number(changedValue);

      if (/^\s*$/.test(changedValue)) {
        forcedValue = '';
        doEmit = false;
      } else if (this.isPortionOfNumberExpression(changedValue)) {
        forcedValue = changedValue;
        doEmit = false;
      } else if (isNaN(converted)) {
        if (this.value === String(Number.POSITIVE_INFINITY) || this.value === String(Number.NEGATIVE_INFINITY)) {
          forcedValue = '';
        }
        doEmit = false;
      } else {
        if (this.isPortionOfNumberExpression(this.valueBeforeChange)) {
          /** transition from intermediate state to emittable state */
          forcedValue = changedValue;
        }
      }
    }
    // if (doEmit && (this.value === changedValue || this.value === String(Number(changedValue)))) {
    //   forcedValue = this.value;
    // }
    this.valueBeforeChange = forcedValue;
    this.toCursorPos = doEmit ? inputEl.selectionStart : null;

    inputEl.value = forcedValue;

    if (doEmit) {
      this.$emit('input', changedValue);
    }

    this.inInputTransaction = true;
    this.$nextTick(() => {
      this.inInputTransaction = false;
    });
  }

  private onInput(event: Event) {
    const inputEl = (event.currentTarget as HTMLInputElement);
    this.handleInput(inputEl);
  }

  private onCompositionStart() {
    this.isComposing = true;
  }

  private onCompositionEnd(event: CompositionEvent) {
    this.isComposing = false;

    const inputEl = (event.currentTarget as HTMLInputElement);
    this.handleInput(inputEl);
  }

  private onFocus(event: MouseEvent) { this.isFocus = !this.disabled; this.$emit('focus', event); }

  private onBlur(event: MouseEvent) {
    this.isFocus = false;
    if (this.numberOnly) {
      const inputEl = (event.currentTarget as HTMLInputElement);
      const currentValue = _trim(inputEl.value);
      if (/^\s*$/.test(currentValue) || isNaN(Number(currentValue))) {
        this.$emit('input', '0');
      } else if (currentValue !== this.value) {
        if (String(Number(currentValue)) === this.value) {
          this.valueBeforeChange = this.value;
          inputEl.value = this.value;
        }
        this.$emit('input', currentValue);
      }
    }
    this.$emit('blur', event);
  }

  private onKeyup(event: KeyboardEvent) {
    this.$emit('keyup', event);
    if (event.key === 'Enter') {
      this.$emit('enter', event);
    }
  }

  private onClickIcon(event: MouseEvent) {
    this.focus();
    this.$emit('icon-click', event);
  }

  public focus() {
    const inputEl = this.$refs.input as HTMLElement;
    if (inputEl) { inputEl.focus(); }
  }

  private mounted() {
    const inputEl = (this.$refs.input as HTMLInputElement);
    inputEl.value = this.value;
  }
}
